import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft,
  UserPlus,
  Mail,
  User,
  Phone,
  Building2,
  Briefcase,
  Handshake,
  Loader2,
  Check,
  Copy
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const ROLE_CONFIG = {
  client: {
    label: 'Client',
    icon: User,
    color: 'blue',
    description: 'Accès à l\'espace client, projets, tickets'
  },
  team_member: {
    label: 'Créatif / Membre équipe',
    icon: Briefcase,
    color: 'purple',
    description: 'Accès aux missions, time tracking, portfolio'
  },
  affiliate: {
    label: 'Apporteur d\'affaires',
    icon: Handshake,
    color: 'orange',
    description: 'Accès au dashboard affilié, commissions'
  }
};

// Specific role assignments for team members
const TEAM_ROLE_OPTIONS = [
  { value: 'project_manager', label: 'Chef de Projet', description: 'Gestion des projets et supervision' },
  { value: 'customer_service', label: 'Service Client', description: 'Support et relation client' },
  { value: 'team_member', label: 'Membre / Créatif', description: 'Exécution des tâches et missions' },
  { value: 'subcontractor', label: 'Sous-traitant', description: 'Missions ponctuelles au forfait' }
];

const CreateUserPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'client',
    assigned_role: 'team_member', // Specific role for team members
    company_name: '',
    specialties: [],
    hourly_rate: 50,
    commission_rate: 10,
    send_email: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.first_name) {
      toast.error('Email et prénom sont requis');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || null,
        role: formData.role,
        send_email: formData.send_email
      };

      if (formData.role === 'client') {
        payload.company_name = formData.company_name;
      } else if (formData.role === 'team_member') {
        payload.specialties = formData.specialties;
        payload.hourly_rate = parseFloat(formData.hourly_rate);
        payload.assigned_role = formData.assigned_role; // Include specific role assignment
      } else if (formData.role === 'affiliate') {
        payload.commission_rate = parseFloat(formData.commission_rate);
      }

      const res = await api.post('/users/create', payload);
      setCreatedUser(res.data);
      toast.success('Compte créé avec succès !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };

  const roleConfig = ROLE_CONFIG[formData.role];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />

      <main className={cn("transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8", collapsed ? "md:ml-20" : "md:ml-64")}
        style={{ paddingTop: "130px" }}
      >
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>Créer un compte</h1>
            <p className="text-sm text-slate-500">Ajoutez un nouveau client, créatif ou apporteur d'affaires</p>
          </div>
        </div>

        <div className="max-w-2xl">
          {createdUser ? (
            <Card className="card">
              <CardHeader>
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <CardTitle className="text-center">Compte créé avec succès !</CardTitle>
                <CardDescription className="text-center">
                  {createdUser.email_sent 
                    ? 'Un email de bienvenue a été envoyé avec les identifiants.' 
                    : 'Partagez les identifiants ci-dessous avec l\'utilisateur.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="font-medium">{createdUser.email}</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => copyToClipboard(createdUser.email)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500">Mot de passe temporaire</p>
                      <p className="font-mono font-bold text-lg">{createdUser.temp_password}</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => copyToClipboard(createdUser.temp_password)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button variant="outline" className="flex-1" onClick={() => { setCreatedUser(null); setFormData({ ...formData, email: '', first_name: '', last_name: '', phone: '' }); }}>
                    Créer un autre compte
                  </Button>
                  <Button className="flex-1" onClick={() => navigate('/clients')} style={{ backgroundColor: theme.colors?.accent }}>
                    Voir les utilisateurs
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="card">
              <CardHeader>
                <CardTitle>Informations du compte</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Role Selection */}
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(ROLE_CONFIG).map(([role, config]) => (
                      <button
                        key={role}
                        type="button"
                        onClick={() => setFormData({ ...formData, role })}
                        className={cn(
                          "p-4 rounded-lg border-2 text-left transition-all",
                          formData.role === role
                            ? `border-${config.color}-500 bg-${config.color}-50`
                            : "border-slate-200 hover:border-slate-300"
                        )}
                      >
                        <config.icon className={`w-6 h-6 mb-2 text-${config.color}-600`} />
                        <p className="font-medium text-sm">{config.label}</p>
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-slate-500">{roleConfig.description}</p>

                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Prénom *</Label>
                      <Input
                        value={formData.first_name}
                        onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                        placeholder="Jean"
                        required
                      />
                    </div>
                    <div>
                      <Label>Nom</Label>
                      <Input
                        value={formData.last_name}
                        onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                        placeholder="Dupont"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      placeholder="jean.dupont@example.com"
                      required
                    />
                  </div>

                  <div>
                    <Label>Téléphone</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>

                  {/* Role-specific fields */}
                  {formData.role === 'client' && (
                    <div>
                      <Label>Entreprise</Label>
                      <Input
                        value={formData.company_name}
                        onChange={e => setFormData({ ...formData, company_name: e.target.value })}
                        placeholder="Nom de l'entreprise"
                      />
                    </div>
                  )}

                  {formData.role === 'team_member' && (
                    <>
                      {/* Role Assignment */}
                      <div>
                        <Label>Rôle / Droits *</Label>
                        <Select
                          value={formData.assigned_role}
                          onValueChange={value => setFormData({ ...formData, assigned_role: value })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Sélectionner un rôle" />
                          </SelectTrigger>
                          <SelectContent>
                            {TEAM_ROLE_OPTIONS.map((role) => (
                              <SelectItem key={role.value} value={role.value}>
                                <div className="flex flex-col">
                                  <span className="font-medium">{role.label}</span>
                                  <span className="text-xs text-slate-500">{role.description}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500 mt-1">Détermine les accès et permissions dans l'application</p>
                      </div>
                      <div>
                        <Label>Spécialités</Label>
                        <Input
                          value={formData.specialties.join(', ')}
                          onChange={e => setFormData({ ...formData, specialties: e.target.value.split(',').map(s => s.trim()) })}
                          placeholder="Graphisme, Web design, Motion design"
                        />
                      </div>
                      <div>
                        <Label>Tarif horaire (€)</Label>
                        <Input
                          type="number"
                          value={formData.hourly_rate}
                          onChange={e => setFormData({ ...formData, hourly_rate: e.target.value })}
                          placeholder="50"
                        />
                      </div>
                    </>
                  )}

                  {formData.role === 'affiliate' && (
                    <div>
                      <Label>Taux de commission (%)</Label>
                      <Input
                        type="number"
                        value={formData.commission_rate}
                        onChange={e => setFormData({ ...formData, commission_rate: e.target.value })}
                        placeholder="10"
                      />
                    </div>
                  )}

                  {/* Email option */}
                  <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                    <Checkbox
                      checked={formData.send_email}
                      onCheckedChange={checked => setFormData({ ...formData, send_email: checked })}
                    />
                    <div>
                      <p className="font-medium text-sm">Envoyer un email de bienvenue</p>
                      <p className="text-xs text-slate-500">L'utilisateur recevra ses identifiants par email</p>
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading} style={{ backgroundColor: theme.colors?.accent }}>
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                    Créer le compte
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default CreateUserPage;
